// API Configuration - PRODUCTION MODE
// This configuration uses the production API with /api prefix
export const API_BASE_URL = 'https://mindspireplanner.com/api';

// Authentication
export const TOKEN_STORAGE_KEY = 'mindspire-planner-token';
export const REFRESH_TOKEN_STORAGE_KEY = 'mindspire-planner-refresh-token';
export const USER_STORAGE_KEY = 'mindspire-planner-user';

// Pagination
export const DEFAULT_PAGE_SIZE = 10;
export const MAX_PAGE_SIZE = 100;

// Time formats
export const TIME_FORMAT = 'HH:mm';
export const DATE_FORMAT = 'yyyy-MM-dd';
export const DATETIME_FORMAT = 'yyyy-MM-dd HH:mm';
export const DISPLAY_DATE_FORMAT = 'MMM dd, yyyy';
export const DISPLAY_DATETIME_FORMAT = 'MMM dd, yyyy HH:mm';

// Days of week
export const DAYS_OF_WEEK = [
  { value: 'MON', label: 'Monday', short: 'Mon' },
  { value: 'TUE', label: 'Tuesday', short: 'Tue' },
  { value: 'WED', label: 'Wednesday', short: 'Wed' },
  { value: 'THU', label: 'Thursday', short: 'Thu' },
  { value: 'FRI', label: 'Friday', short: 'Fri' },
  { value: 'SAT', label: 'Saturday', short: 'Sat' },
  { value: 'SUN', label: 'Sunday', short: 'Sun' },
] as const;

// Study block statuses
export const STUDY_BLOCK_STATUSES = [
  { value: 'PLANNED', label: 'Planned', color: 'blue' },
  { value: 'IN_PROGRESS', label: 'In Progress', color: 'yellow' },
  { value: 'DONE', label: 'Completed', color: 'green' },
  { value: 'CANCELLED', label: 'Cancelled', color: 'gray' },
  { value: 'SKIPPED', label: 'Skipped', color: 'orange' },
  { value: 'OVERDUE', label: 'Overdue', color: 'red' },
] as const;

// Exam types
export const EXAM_TYPES = [
  { value: 'REGULAR', label: 'Regular Test' },
  { value: 'MIDTERM', label: 'Midterm Exam' },
  { value: 'FINAL', label: 'Final Exam' },
  { value: 'AP', label: 'AP Exam' },
  { value: 'STANDARDIZED', label: 'Standardized Test' },
  { value: 'QUIZ', label: 'Quiz' },
  { value: 'PROJECT', label: 'Project' },
  { value: 'PRESENTATION', label: 'Presentation' },
] as const;

// Priority levels
export const PRIORITY_LEVELS = [
  { value: 'LOW', label: 'Low', color: 'green' },
  { value: 'MEDIUM', label: 'Medium', color: 'yellow' },
  { value: 'HIGH', label: 'High', color: 'orange' },
  { value: 'CRITICAL', label: 'Critical', color: 'red' },
] as const;

// User roles
export const USER_ROLES = [
  { value: 'ADMIN', label: 'Administrator' },
  { value: 'PARENT', label: 'Parent' },
  { value: 'STUDENT', label: 'Student' },
] as const;

// Grade levels
export const GRADE_LEVELS = Array.from({ length: 12 }, (_, i) => ({
  value: i + 1,
  label: `Grade ${i + 1}`,
}));

// Subjects (common subjects for study blocks)
export const COMMON_SUBJECTS = [
  'Mathematics',
  'English',
  'Science',
  'History',
  'Geography',
  'Physics',
  'Chemistry',
  'Biology',
  'Computer Science',
  'Art',
  'Music',
  'Physical Education',
  'Foreign Language',
  'Social Studies',
] as const;

// Calendar colors for different event types
export const EVENT_COLORS = {
  routine: '#3b82f6', // blue
  'study-block': '#10b981', // green
  exam: '#f59e0b', // amber
} as const;

// Theme colors
export const THEME_COLORS = {
  primary: 'hsl(221.2 83.2% 53.3%)',
  secondary: 'hsl(210 40% 96%)',
  accent: 'hsl(210 40% 96%)',
  destructive: 'hsl(0 84.2% 60.2%)',
  success: 'hsl(142.1 76.2% 36.3%)',
  warning: 'hsl(38.4 92.1% 50.2%)',
  info: 'hsl(199.4 89.2% 48.4%)',
} as const;

// Notification types
export const NOTIFICATION_TYPES = [
  { value: 'INFO', label: 'Information', color: 'blue' },
  { value: 'SUCCESS', label: 'Success', color: 'green' },
  { value: 'WARNING', label: 'Warning', color: 'yellow' },
  { value: 'ERROR', label: 'Error', color: 'red' },
] as const;

// Local storage keys
export const STORAGE_KEYS = {
  AUTH_TOKEN: 'mindspire-planner-auth-token',
  REFRESH_TOKEN: 'mindspire-planner-refresh-token',
  USER_DATA: 'mindspire-planner-user-data',
  THEME: 'mindspire-planner-theme',
  SIDEBAR_COLLAPSED: 'mindspire-planner-sidebar-collapsed',
  CALENDAR_VIEW: 'mindspire-planner-calendar-view',
  CALENDAR_FILTERS: 'mindspire-planner-calendar-filters',
} as const;

// Query keys for React Query
export const QUERY_KEYS = {
  // Auth
  ME: ['auth', 'me'] as const,
  
  // Students
  STUDENTS: ['students'] as const,
  STUDENT: (id: number) => ['students', id] as const,
  STUDENT_DASHBOARD: (id: number) => ['students', id, 'dashboard'] as const,
  STUDENT_PROGRESS: (id: number) => ['students', id, 'progress'] as const,
  
  // Parents
  PARENTS: ['parents'] as const,
  PARENT: (id: number) => ['parents', id] as const,
  PARENT_DASHBOARD: (id: number) => ['parents', id, 'dashboard'] as const,
  PARENT_STUDENTS: (id: number) => ['parents', id, 'students'] as const,
  
  // Routines
  ROUTINES: ['routines'] as const,
  ROUTINE: (id: number) => ['routines', id] as const,
  STUDENT_ROUTINES: (studentId: number) => ['routines', 'student', studentId] as const,
  ROUTINES_BY_DAY: (studentId: number, day: string) => ['routines', 'student', studentId, 'day', day] as const,
  
  // Study Blocks
  STUDY_BLOCKS: ['study-blocks'] as const,
  STUDY_BLOCK: (id: number) => ['study-blocks', id] as const,
  STUDENT_STUDY_BLOCKS: (studentId: number) => ['study-blocks', 'student', studentId] as const,
  STUDY_BLOCKS_BY_DATE: (studentId: number, date: string) => ['study-blocks', 'student', studentId, 'date', date] as const,
  
  // Exams
  EXAMS: ['exams'] as const,
  EXAM: (id: number) => ['exams', id] as const,
  UPCOMING_EXAMS: ['exams', 'upcoming'] as const,
  
  // Calendar
  CALENDAR_EVENTS: (studentId: number, startDate: string, endDate: string) => 
    ['calendar', 'events', studentId, startDate, endDate] as const,
  
  // Analytics
  DASHBOARD_STATS: ['analytics', 'dashboard'] as const,
  STUDENT_STATS: (studentId: number) => ['analytics', 'student', studentId] as const,
  
  // Family
  FAMILY_LINKS: ['family', 'links'] as const,
  FAMILY_CODES: ['family', 'codes'] as const,
} as const;
